import { CSSProperties } from "@material-ui/core/styles/withStyles";

// styles
import { greys, colors, helpers } from "@clockwise/web-commons/src/styles/color.styles";
import { fontFamily, fontFamilySans, weight } from "@clockwise/web-commons/src/styles/type.styles";

const PRICING_PAGE_BREAKPOINT_WIDTH = 1000;

const anchor: CSSProperties = {
  color: greys.standard,
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
};

const bold: CSSProperties = {
  fontWeight: "bold",
};

const wrapper: CSSProperties = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: greys.white,
  width: "100%",
};

const container: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: 40,
  borderRadius: 10,
  maxWidth: 1300,
  width: "100%",
};

const nonStandardPlanCallout: CSSProperties = {
  background: helpers.opacity(colors.blueCool.lightest, 0.3),
  borderRadius: 5,
  padding: "20px 30px",
  margin: "0 32px 20px",
  fontWeight: "bold",
  fontSize: 14,
  width: "100%",
  textAlign: "center",
  lineHeight: 1.5,
  [`@media (max-width: ${PRICING_PAGE_BREAKPOINT_WIDTH - 1}px)`]: {
    maxWidth: 500,
  },
};

const promoContainer: CSSProperties = {
  background: helpers.opacity(colors.blueCool.lightest, 0.3),
  padding: 15,
  width: "100%",
  maxWidth: 900,
  fontSize: 16,
  lineHeight: 2,
  textAlign: "center",
  color: greys.standard,
  position: "absolute",
  top: 0,
};

const headerContainer: CSSProperties = {};

const headerRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "fit-content",
  margin: "0 auto",
};

const headerUsageSummary: CSSProperties = {
  fontSize: 16,
  lineHeight: 1.6,
  textAlign: "center",
  color: greys.slate,
  maxWidth: 675,
  width: "100%",
  margin: "0 auto 35px",
};

const pills: CSSProperties = {
  margin: "0 auto",
  width: 500,
  alignSelf: "flex-start",
  display: "flex",
  flexDirection: "row",
  borderRadius: 20,
  minHeight: 43,
  background: greys.palePaleGrey,
  padding: 4,
  marginBottom: 34,

  "& > .pill": {
    cursor: "pointer",
    borderRadius: 20,
    display: "flex",
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    color: colors.blueDusk.standard,
    fontSize: "15px",
    fontWeight: "bold",
    marginRight: 4,

    "& svg": {
      marginRight: 10,
    },

    "&.active": {
      cursor: "default",
      background: greys.white,
      boxShadow: "0px 0px 3px rgba(0, 0, 0, 0.15)",

      "&:hover": {
        background: greys.white,
        boxShadow: "0px 0px 3px rgba(0, 0, 0, 0.15)",
      },
    },

    "&:hover": {
      background: greys.paleGrey,
    },

    "&:last-child": {
      marginRight: 0,
    },
  },
};

const green: CSSProperties = {
  color: colors.green.darker,
};

const pricingGridContainer: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  width: "100%",
  [`@media (max-width: ${PRICING_PAGE_BREAKPOINT_WIDTH - 1}px)`]: {
    flexDirection: "column",
    alignItems: "center",
  },
};

const tier: CSSProperties = {
  maxWidth: 305,
  minWidth: 225,
  flex: 1,
  height: 640,
  position: "relative",
  padding: "64px 16px 30px",
  border: `1px solid ${greys.borderGrey}`,
  "&:first-child": {
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
  },
  "&:last-child": {
    borderTopRightRadius: 5,
    borderBottomRightRadius: 5,
  },
  [`@media (min-width: ${PRICING_PAGE_BREAKPOINT_WIDTH}px)`]: {
    "&:nth-child(-n+3)": {
      borderRight: "none",
    },
  },
  [`@media (max-width: ${PRICING_PAGE_BREAKPOINT_WIDTH - 1}px)`]: {
    width: "100%",
    maxWidth: 500,
    margin: "15px 0",
  },
};

const popularBanner: CSSProperties = {
  fontFamily,
  position: "absolute",
  height: 22,
  top: 0,
  left: 0,
  right: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: colors.green.standard,
  color: greys.white,
  fontWeight: "bold",
  fontSize: 11,
  textTransform: "uppercase",
};

const proTrialLessThan5DaysBanner: CSSProperties = {
  ...popularBanner,
  background: colors.peach.darker,
};

const tierTitle: CSSProperties = {
  fontFamily,
  fontWeight: "bold",
  fontSize: 24,
  lineHeight: "36px",
  color: greys.standard,
  textAlign: "center",
};

const tierCost: CSSProperties = {
  fontFamily,
  fontWeight: "bold",
  fontSize: 36,
  color: greys.standard,
  textAlign: "center",
  display: "flex",
  alignItems: "baseline",
  justifyContent: "center",
  padding: "30px 0 0",
  marginBottom: 10,
  height: 66,
  width: 225,
  margin: "0 auto",
};

const dollar: CSSProperties = {
  display: "flex",
  color: greys.slate,
  fontSize: 24,
  alignSelf: "flex-start",
};

const currency: CSSProperties = {
  display: "flex",
  color: greys.slate,
  margin: "5px",
  fontSize: 12,
  alignSelf: "flex-end",
};

const previousPrice: CSSProperties = {
  fontFamily: fontFamilySans,
  fontSize: 37,
  fontWeight: weight.std,
  color: greys.blueGrey,
  textDecoration: "line-through",
  margin: "0 15px 0 30px",
};

const percentDiscout: CSSProperties = {
  fontFamily: fontFamilySans,
  fontWeight: weight.std,
  fontSize: 12,
  color: colors.blueSky.darker,
  background: helpers.opacity(colors.blueSky.lightest, 0.5),
  borderRadius: 2,
  padding: 5,
  marginTop: 15,
  textTransform: "uppercase",
};

const letsTalk: CSSProperties = {
  display: "flex",
  alignItems: "center",
  fontSize: 28,
  fontFamily: fontFamilySans,
  height: 75,
};

const tierCostUnit: CSSProperties = {
  fontSize: 12,
  lineHeight: "21px",
  color: greys.slate,
  textAlign: "center",
  height: 44,
  overflow: "hidden",
  textOverflow: "ellipsis",
  marginBottom: 15,
};

const tierButton: CSSProperties = {
  fontSize: 14,
  lineHeight: 1.3,
  color: greys.standard,
  textAlign: "center",
  margin: "5px auto 15px",
  maxWidth: 219,
  width: "100%",
  height: 38,
};

const secondaryButton: CSSProperties = {
  color: greys.slate,
};

const primaryButton: CSSProperties = {
  color: greys.white,
  background: colors.green.standard,
  "&:hover": {
    background: helpers.opacity(colors.green.standard, 0.9),
  },
};

const primaryContactUs: CSSProperties = {
  color: colors.green.standard,
  borderColor: colors.green.standard,
  background: greys.white,
  "&:hover": {
    background: helpers.opacity(colors.green.standard, 0.1),
  },
};

const primaryContactUsLabel: CSSProperties = {
  color: colors.green.standard,
};

const disabledButton: CSSProperties = {
  opacity: 0.7,
  pointerEvents: "none",
};

const tierDescription: CSSProperties = {
  fontSize: 12,
  lineHeight: 1.3,
  color: greys.standard,
  opacity: 0.6,
  textAlign: "center",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const tierDescriptionShort: CSSProperties = {
  ...tierDescription,
  paddingBottom: 15,
};

const divider: CSSProperties = {
  margin: "15px 0",
};

const tierFeatureHeader: CSSProperties = {
  fontSize: 14,
  fontWeight: "bold",
  color: greys.slate,
  marginLeft: 5,
  marginBottom: 15,
};

const tierFeatureBullet: CSSProperties = {
  height: 14,
  margin: "0 5px 0 0",
  color: greys.slate,
  lineHeight: "14px",
  fontSize: 18,
  marginLeft: 5,
};

const tierFeature: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  fontSize: 14,
  lineHeight: 1.5,
  color: greys.standard,
  marginBottom: 12,
};

const tierSubFeature: CSSProperties = {
  marginLeft: 30,
};

const tierFeatureTitleContainer: CSSProperties = {
  display: "block",
};

const tierFeatureTitle: CSSProperties = {
  display: "flex",
  alignItems: "center",
};

const tierFeatureSubtitle: CSSProperties = {
  fontFamily: fontFamilySans,
  fontWeight: weight.std,
  fontSize: 13,
  lineHeight: "22px",
  marginLeft: 20,
};

const featureComparisonToggle: CSSProperties = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: 20,
  lineHeight: "14px",
  color: colors.blueDusk.darker,
  margin: "75px 0",
  [`@media (max-width: ${PRICING_PAGE_BREAKPOINT_WIDTH - 1}px)`]: {
    display: "none",
  },
};

const featureComparisonToggleIcon: CSSProperties = {
  color: colors.blueDusk.darker,
};

const featureComparisonContainer: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  marginBottom: 50,
  paddingLeft: 8,
  transition: "all 0.5s ease-in-out",
  maxHeight: 1500,
  overflow: "hidden",
  [`@media (max-width: ${PRICING_PAGE_BREAKPOINT_WIDTH - 1}px)`]: {
    display: "none",
  },
};

const expanded: CSSProperties = {};

const collapsed: CSSProperties = {
  maxHeight: 0,
};

const featureComparisonRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-evenly",
  height: 50,
  lineHeight: 1.4,
  "&:nth-last-child(2)": {
    "& > div": {
      borderRadius: "0 0 5px 5px",
    },
  },
};

const featureHeader: CSSProperties = {
  fontFamily,
  fontWeight: "bold",
  borderBottom: `1px solid ${greys.cloudyBlue}`,
  borderRadius: "5px 5px 0px 0px",
};

const featureTitle: CSSProperties = {
  flex: 5,
  height: "100%",
  fontSize: 16,
  lineHeight: "14px",
  color: greys.slate,
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
};

const featureTitleUnderline: CSSProperties = {
  borderBottom: `1px dashed ${greys.slate}`,
  cursor: "pointer",
};

const borderRed: CSSProperties = {
  borderColor: colors.peach.darker,
};

const featureTitleBold: CSSProperties = {
  fontWeight: "bold",
  color: greys.standard,
};

const featureTitleBorder: CSSProperties = {
  borderBottom: `1px solid ${greys.cloudyBlue}`,
};

const baseFeature: CSSProperties = {
  flex: 3,
  width: 200,
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 10px",
  fontSize: 15,
  color: greys.standard,
};

const freeFeature: CSSProperties = {
  ...baseFeature,
  background: greys.palePaleGrey,
};

const proFeature: CSSProperties = {
  ...baseFeature,
  background: helpers.opacity(colors.green.standard, 0.1),
};

const businessFeature: CSSProperties = {
  ...baseFeature,
  background: helpers.opacity("#44C3D7", 0.1),
};

const enterpriseFeature: CSSProperties = {
  ...baseFeature,
  background: "#F3F9FF",
};

const comparisonDescription: CSSProperties = {
  color: greys.blueGrey,
};

const grey: CSSProperties = {
  color: greys.standard,
};

const red: CSSProperties = {
  color: colors.peach.darker,
};

const scheduleAssistsRow: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: 15,
  textAlign: "center",
};

const scheduleAssistsIcon: CSSProperties = {
  width: 32,
  height: 32,
  marginBottom: 2,
};

const degradeConfirmationButton: CSSProperties = {
  // backgroundColor: colors.red.standard,
  // color: greys.white,
  color: colors.green.standard,
  "&:hover": {
    backgroundColor: helpers.lighten(colors.green.standard, 0.75),
  },
};

const faqContainer: CSSProperties = {
  background: helpers.lighten(colors.blueCool.lightest, 0.75),
  textAlign: "center",
  padding: "50px 100px",
  maxWidth: 900,
};

const emoji: CSSProperties = {
  height: 16,
  marginTop: -1,
  marginRight: 3,
};

const bannerEmoji: CSSProperties = {
  ...emoji,
  paddingRight: 4,
};

export const styles = {
  wrapper,
  container,
  anchor,
  bold,
  nonStandardPlanCallout,
  promoContainer,
  headerContainer,
  headerRow,
  headerUsageSummary,
  pills,
  green,
  pricingGridContainer,
  tier,
  popularBanner,
  proTrialLessThan5DaysBanner,
  tierTitle,
  tierCost,
  dollar,
  currency,
  previousPrice,
  percentDiscout,
  letsTalk,
  tierCostUnit,
  tierButton,
  secondaryButton,
  primaryButton,
  primaryContactUs,
  primaryContactUsLabel,
  disabledButton,
  tierDescription,
  tierDescriptionShort,
  divider,
  tierFeatureHeader,
  tierFeatureBullet,
  tierFeature,
  tierSubFeature,
  tierFeatureTitleContainer,
  tierFeatureTitle,
  tierFeatureSubtitle,
  featureComparisonToggle,
  featureComparisonToggleIcon,
  featureComparisonContainer,
  expanded,
  collapsed,
  featureComparisonRow,
  featureHeader,
  featureTitle,
  featureTitleUnderline,
  borderRed,
  featureTitleBold,
  featureTitleBorder,
  baseFeature,
  freeFeature,
  proFeature,
  businessFeature,
  enterpriseFeature,
  comparisonDescription,
  grey,
  red,
  scheduleAssistsRow,
  scheduleAssistsIcon,
  degradeConfirmationButton,
  faqContainer,
  emoji,
  bannerEmoji,
};
